import React, { useState, useEffect, useMemo, useRef } from 'react';
import salairesApi from '../services/salaires';
import './MasseSalariale.css';

const MOIS_COURT = ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aou', 'Sep', 'Oct', 'Nov', 'Dec'];

const eur = (n) => `${Math.round(n).toLocaleString('fr-FR')} €`;
const keur = (n) => (Math.abs(n) >= 1000 ? `${Math.round(n / 1000)} k€` : `${Math.round(n)} €`);
const pct = (n) => `${n >= 0 ? '+' : ''}${n.toFixed(1)} %`;

// Natures de cout, dans l'ordre fixe des emplacements categoriels validés.
// L'ordre ne varie jamais : une nature garde sa couleur quel que soit le filtre.
const NATURES = [
  { cle: 'brut_fixe', label: 'Brut fixe', couleur: 'var(--serie-1)' },
  { cle: 'variable', label: 'Variable mensuel', couleur: 'var(--serie-2)' },
  { cle: 'primes_annuelles', label: 'Primes annuelles', couleur: 'var(--serie-3)' },
  { cle: 'exceptionnel', label: 'Exceptionnel', couleur: 'var(--serie-6)' },
  { cle: 'cotisations', label: 'Cotisations patronales', couleur: 'var(--serie-4)' },
  { cle: 'non_soumis', label: 'Non soumis', couleur: 'var(--serie-5)' },
];

// Part recurrente : ce qui revient tous les mois. Isoler les primes annuelles
// et l'exceptionnel evite que le 13e mois n'ecrase l'echelle et ne masque la
// tendance de fond des dix-huit autres mois.
const recurrent = (m) => m.masse - (m.primes_annuelles || 0) - (m.exceptionnel || 0);

function Tuile({ libelle, valeur, variation, detail, alerte }) {
  return (
    <div className={`ms-tuile ${alerte ? 'ms-tuile-alerte' : ''}`}>
      <div className="ms-tuile-libelle">{libelle}</div>
      <div className="ms-tuile-valeur">{valeur}</div>
      <div className="ms-tuile-pied">
        {variation !== null && variation !== undefined && (
          <span className={`ms-variation ${variation > 0.05 ? 'hausse' : variation < -0.05 ? 'baisse' : 'stable'}`}>
            {pct(variation)}
          </span>
        )}
        {detail && <span className="ms-tuile-detail">{detail}</span>}
      </div>
    </div>
  );
}

// Courbe simple, une seule serie : pas de legende, un libelle direct sur le dernier point.
function Courbe({ points, series, format, hauteur = 150, baseZero = true }) {
  const [survol, setSurvol] = useState(null);
  const ref = useRef(null);
  if (!points.length) return <div className="ms-vide">Aucune donnee</div>;

  const L = 640, H = hauteur, mg = { h: 12, b: 24, g: 46, d: 52 };
  const vals = points.flatMap(p => series.map(se => p[se.cle]));
  // Base non nulle sur les series ou l'amplitude utile est faible devant le
  // niveau : sinon la courbe s'aplatit et ne dit plus rien.
  const brut = Math.min(...vals);
  const min = baseZero ? Math.min(0, brut) : brut - (Math.max(...vals) - brut) * 0.25;
  const max = Math.max(...vals) * 1.06 || 1;
  const x = (i) => mg.g + (i * (L - mg.g - mg.d)) / Math.max(1, points.length - 1);
  const y = (v) => mg.h + (1 - (v - min) / (max - min || 1)) * (H - mg.h - mg.b);
  const trace = (cle) => points.map((p, i) => `${i ? 'L' : 'M'}${x(i)},${y(p[cle])}`).join(' ');

  const surMouvement = (e) => {
    const r = ref.current.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * L;
    let best = 0;
    points.forEach((_, i) => { if (Math.abs(x(i) - px) < Math.abs(x(best) - px)) best = i; });
    setSurvol(best);
  };

  return (
    <div className="ms-graphe">
      <svg ref={ref} viewBox={`0 0 ${L} ${H}`} className="ms-svg"
        onMouseMove={surMouvement} onMouseLeave={() => setSurvol(null)}>
        {[0, 0.5, 1].map(t => {
          const v = min + t * (max - min);
          return (
            <g key={t}>
              <line x1={mg.g} x2={L - mg.d} y1={y(v)} y2={y(v)} className="ms-grille" />
              <text x={mg.g - 8} y={y(v) + 3} className="ms-axe" textAnchor="end">{format(v)}</text>
            </g>
          );
        })}
        {series.length === 1 && (
          <path d={`${trace(series[0].cle)} L${x(points.length - 1)},${y(min)} L${x(0)},${y(min)} Z`}
            fill={series[0].couleur} opacity="0.12" />
        )}
        {series.map(se => (
          <path key={se.cle} d={trace(se.cle)} fill="none" stroke={se.couleur}
            strokeWidth="2" strokeLinejoin="round" strokeDasharray={se.pointille ? '5 4' : undefined} />
        ))}
        {survol !== null && (
          <line x1={x(survol)} x2={x(survol)} y1={mg.h} y2={H - mg.b} className="ms-curseur" />
        )}
        {series.map(se => (
          <circle key={se.cle} cx={x(points.length - 1)} cy={y(points[points.length - 1][se.cle])}
            r="4" fill={se.couleur} stroke="#fff" strokeWidth="2" />
        ))}
        <text x={x(points.length - 1) + 9} y={y(points[points.length - 1][series[0].cle]) + 4} className="ms-label-direct">
          {format(points[points.length - 1][series[0].cle])}
        </text>
        {points.map((p, i) => (
          (i % Math.ceil(points.length / 7) === 0 || i === points.length - 1) && (
            <text key={i} x={x(i)} y={H - 6} className="ms-axe" textAnchor="middle">{p.libelle}</text>
          )
        ))}
      </svg>
      {survol !== null && (
        <div className="ms-infobulle" style={{ left: `${(x(survol) / L) * 100}%` }}>
          <strong>{points[survol].libelle}</strong>
          {series.map(se => (
            <span key={se.cle}>
              <i className="ms-puce" style={{ background: se.couleur }} />
              {se.label} <b>{format(points[survol][se.cle])}</b>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// Barres empilees : 5 natures, ordre fixe, ecart de 2px entre segments
function BarresEmpilees({ mois }) {
  const [survol, setSurvol] = useState(null);
  if (!mois.length) return <div className="ms-vide">Aucune donnee</div>;
  const totaux = mois.map(m => NATURES.reduce((s, n) => s + Math.max(0, m[n.cle] || 0), 0));
  const max = Math.max(...totaux) || 1;
  return (
    <div className="ms-empile">
      {mois.map((m, i) => {
        let cumul = 0;
        return (
          <div className="ms-empile-col" key={`${m.annee}-${m.mois}`}
            onMouseEnter={() => setSurvol(i)} onMouseLeave={() => setSurvol(null)}>
            <div className="ms-empile-total">{keur(totaux[i])}</div>
            <div className="ms-empile-barre">
              {NATURES.map(n => {
                const v = Math.max(0, m[n.cle] || 0);
                if (v <= 0) return null;
                const h = (v / max) * 100;
                const bas = (cumul / max) * 100;
                cumul += v;
                // Segments jointifs, separes par un simple filet de la couleur
                // de fond : des ecarts de 2px faisaient flotter les segments fins.
                return (
                  <div key={n.cle} className="ms-segment"
                    style={{ background: n.couleur, height: `${h}%`, bottom: `${bas}%` }} />
                );
              })}
            </div>
            <div className="ms-empile-label">{MOIS_COURT[m.mois - 1]}</div>
            {survol === i && (
              <div className="ms-infobulle ms-infobulle-haut">
                <strong>{MOIS_COURT[m.mois - 1]} {m.annee}</strong>
                {NATURES.map(n => (
                  <span key={n.cle}>
                    <i className="ms-puce" style={{ background: n.couleur }} />
                    {n.label} <b>{keur(m[n.cle] || 0)}</b>
                  </span>
                ))}
                <span className="ms-infobulle-total">Total <b>{keur(totaux[i])}</b></span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Variation d'un mois sur l'autre, decomposee en effet effectif et effet cout
// unitaire. Les deux effets s'additionnent exactement a l'ecart total.
function Variations({ mois }) {
  const [survol, setSurvol] = useState(null);
  const lignes = useMemo(() => mois.slice(1).map((m, i) => {
    const p = mois[i];
    const cmM = m.effectif ? m.masse / m.effectif : 0;
    const cmP = p.effectif ? p.masse / p.effectif : 0;
    const ecart = m.masse - p.masse;
    return {
      m,
      libelle: `${MOIS_COURT[m.mois - 1]} ${String(m.annee).slice(2)}`,
      ecart,
      pct: p.masse ? (ecart / p.masse) * 100 : 0,
      effetEffectif: (m.effectif - p.effectif) * cmP,
      effetCout: (cmM - cmP) * m.effectif,
      dEffectif: m.effectif - p.effectif,
      dPrimes: (m.primes_annuelles || 0) - (p.primes_annuelles || 0),
    };
  }), [mois]);
  if (!lignes.length) return <div className="ms-vide">Historique insuffisant</div>;

  const amplitude = Math.max(...lignes.map(l => Math.abs(l.ecart))) || 1;
  return (
    <div className="ms-variations">
      {lignes.map((l, i) => {
        const h = (Math.abs(l.ecart) / amplitude) * 50;
        const positif = l.ecart >= 0;
        return (
          <div className="ms-var-col" key={l.libelle}
            onMouseEnter={() => setSurvol(i)} onMouseLeave={() => setSurvol(null)}>
            <div className="ms-var-zone">
              <div className={`ms-var-barre ${positif ? 'hausse' : 'baisse'}`}
                style={{ height: `${h}%`, [positif ? 'bottom' : 'top']: '50%' }} />
              <div className="ms-var-zero" />
            </div>
            <div className={`ms-var-pct ${positif ? 'hausse' : 'baisse'}`}>
              {l.pct >= 0 ? '+' : ''}{l.pct.toFixed(0)} %
            </div>
            <div className="ms-var-mois">{MOIS_COURT[l.m.mois - 1]}</div>
            {survol === i && (
              <div className="ms-infobulle ms-infobulle-haut">
                <strong>{l.libelle} vs mois precedent</strong>
                <span>Ecart total <b>{(l.ecart >= 0 ? '+' : '') + keur(l.ecart)}</b></span>
                <span>
                  <i className="ms-puce" style={{ background: 'var(--serie-1)' }} />
                  Effet effectif ({l.dEffectif >= 0 ? '+' : ''}{l.dEffectif} pers.)
                  {' '}<b>{(l.effetEffectif >= 0 ? '+' : '') + keur(l.effetEffectif)}</b>
                </span>
                <span>
                  <i className="ms-puce" style={{ background: 'var(--serie-2)' }} />
                  Effet cout unitaire <b>{(l.effetCout >= 0 ? '+' : '') + keur(l.effetCout)}</b>
                </span>
                {Math.abs(l.dPrimes) > 1000 && (
                  <span className="ms-infobulle-total">
                    dont primes annuelles <b>{(l.dPrimes >= 0 ? '+' : '') + keur(l.dPrimes)}</b>
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Comparatif poste x entite : un seul chiffre par case, le cumul de la periode.
// Les postes sont regroupes sous leur activite, avec un sous-total par activite
// qui sert de reperes quand la liste s'allonge.
function Comparatif({ entites, groupes }) {
  const [replies, setReplies] = useState({});
  if (!entites.length || !groupes.length) return <div className="ms-vide">Aucune donnee sur la periode</div>;

  const totalParEntite = {};
  entites.forEach(e => { totalParEntite[e] = groupes.reduce((a, g) => a + (g.cellules[e] || 0), 0); });
  const totalGeneral = groupes.reduce((a, g) => a + g.total, 0);
  const cellule = (v) => (v ? eur(v) : <span className="ms-comp-vide">—</span>);

  return (
    <div className="ms-comparatif-cadre">
      <table className="ms-comparatif">
        <thead>
          <tr>
            <th className="ms-comp-service">Activite / poste</th>
            {entites.map(e => <th key={e}>{e}</th>)}
            <th className="ms-comp-total">Total</th>
          </tr>
        </thead>
        {groupes.map(g => {
          const replie = !!replies[g.activite];
          return (
            <tbody key={g.activite}>
              <tr className="ms-comp-groupe" onClick={() => setReplies(r => ({ ...r, [g.activite]: !r[g.activite] }))}>
                <td className="ms-comp-service">
                  <span className="ms-comp-fleche">{replie ? '▶' : '▼'}</span>{g.activite}
                </td>
                {entites.map(e => <td key={e}>{cellule(g.cellules[e])}</td>)}
                <td className="ms-comp-total">{eur(g.total)}</td>
              </tr>
              {!replie && g.lignes.map(l => (
                <tr key={l.poste} className={l.poste === 'Sans poste' ? 'ms-comp-orphelin' : ''}>
                  <td className="ms-comp-service ms-comp-poste">{l.poste}</td>
                  {entites.map(e => <td key={e}>{cellule(l.cellules[e])}</td>)}
                  <td className="ms-comp-total">{eur(l.total)}</td>
                </tr>
              ))}
            </tbody>
          );
        })}
        <tfoot>
          <tr>
            <td className="ms-comp-service">Total</td>
            {entites.map(e => <td key={e}>{eur(totalParEntite[e])}</td>)}
            <td className="ms-comp-total">{eur(totalGeneral)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// Barres horizontales avec valeurs toujours visibles (regle de relief)
function BarresHorizontales({ lignes, couleur, format }) {
  if (!lignes.length) return <div className="ms-vide">Aucune donnee</div>;
  const max = Math.max(...lignes.map(l => l.valeur)) || 1;
  return (
    <div className="ms-barres-h">
      {lignes.map(l => (
        <div className="ms-barre-h" key={l.libelle}>
          <div className="ms-barre-h-libelle">{l.libelle}</div>
          <div className="ms-barre-h-piste">
            <div className="ms-barre-h-remplissage" style={{ width: `${(l.valeur / max) * 100}%`, background: couleur }} />
          </div>
          <div className="ms-barre-h-valeur">{format(l.valeur)}</div>
          {l.detail && <div className="ms-barre-h-detail">{l.detail}</div>}
        </div>
      ))}
    </div>
  );
}

export default function MasseSalariale({ isAdmin }) {
  const [data, setData] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [etabs, setEtabs] = useState([]);
  const [periode, setPeriode] = useState({ debut: '', fin: '' });
  const [refEtabs, setRefEtabs] = useState([]);
  const refCharge = useRef(false);

  useEffect(() => {
    let annule = false;
    setChargement(true);
    salairesApi.getMasseSalariale({
      ...(etabs.length ? { etabs: etabs.join(',') } : {}),
      ...(periode.debut && periode.fin ? { debut: periode.debut, fin: periode.fin } : {}),
    })
      .then(r => {
        if (annule) return;
        setData(r.data);
        if (!refCharge.current) { refCharge.current = true; setRefEtabs(r.data.etablissements); }
        setErreur(null);
      })
      .catch(e => !annule && setErreur(e.response?.data?.error || 'Chargement impossible'))
      .finally(() => !annule && setChargement(false));
    return () => { annule = true; };
  }, [etabs, periode]);

  const parSociete = useMemo(() => {
    const m = new Map();
    refEtabs.forEach(e => {
      if (!m.has(e.societe)) m.set(e.societe, []);
      m.get(e.societe).push(e);
    });
    return [...m.entries()];
  }, [refEtabs]);

  const calc = useMemo(() => {
    if (!data || !data.mois.length) return null;
    const m = data.mois;
    const dernier = m[m.length - 1];
    const precedent = m.length > 1 ? m[m.length - 2] : null;
    const memeMoisAnneePrec = m.find(x => x.annee === dernier.annee - 1 && x.mois === dernier.mois);
    const varie = (a, b) => (b && b !== 0 ? ((a - b) / Math.abs(b)) * 100 : null);
    const brutTotal = dernier.brut_fixe + dernier.variable + dernier.exceptionnel;
    const coutMoyen = dernier.effectif ? dernier.masse / dernier.effectif : 0;
    const coutMoyenPrec = precedent && precedent.effectif ? precedent.masse / precedent.effectif : null;
    const anc = data.anciennete_mediane_mois || data.anciennete_moyenne_mois;
    const senior = data.anciennete.find(a => a.tranche === 'Plus de 20 ans')?.n || 0;
    const totalPresents = data.anciennete.reduce((s, a) => s + a.n, 0);
    return {
      dernier, precedent, memeMoisAnneePrec,
      varMasse: varie(dernier.masse, precedent?.masse),
      varEffectif: varie(dernier.effectif, precedent?.effectif),
      varAn: varie(dernier.masse, memeMoisAnneePrec?.masse),
      coutMoyen, varCoutMoyen: varie(coutMoyen, coutMoyenPrec),
      tauxCharges: brutTotal ? (dernier.cotisations / brutTotal) * 100 : 0,
      poidsAbsences: brutTotal ? (Math.abs(dernier.absences) / brutTotal) * 100 : 0,
      ancienneteAns: anc ? anc / 12 : 0,
      senior, totalPresents,
      partSenior: totalPresents ? (senior / totalPresents) * 100 : 0,
      masse12: m.slice(-12).reduce((s, x) => s + x.masse, 0),
    };
  }, [data]);

  if (!isAdmin) return <div className="ms-vide">Reserve aux administrateurs.</div>;
  if (chargement && !data) return <div className="ms-vide">Chargement…</div>;
  if (erreur) return <div className="ms-vide">{erreur}</div>;
  if (!calc) return <div className="ms-vide">Aucune donnee de paie importee.</div>;

  const { dernier } = calc;
  const moisDispo = data.mois.map(m => ({
    cle: `${m.annee}-${String(m.mois).padStart(2, '0')}`,
    label: `${MOIS_COURT[m.mois - 1]} ${m.annee}`,
  }));
  const douzeMois = data.mois.slice(-12);
  const basculer = (e) => setEtabs(v => (v.includes(e) ? v.filter(x => x !== e) : [...v, e]));
  const selSociete = (liste) => {
    const codes = liste.map(x => x.etab);
    const tousDedans = codes.every(c => etabs.includes(c));
    setEtabs(v => (tousDedans ? v.filter(c => !codes.includes(c)) : [...new Set([...v, ...codes])]));
  };

  const alertes = [];
  if (calc.partSenior >= 35) alertes.push(`${calc.senior} personnes sur ${calc.totalPresents} ont plus de 20 ans d'anciennete (${Math.round(calc.partSenior)} %) : anticipez les departs et le poids des primes d'anciennete.`);
  if (calc.varAn !== null && calc.varAn > 3) alertes.push(`Masse en hausse de ${pct(calc.varAn)} sur un an a periode comparable.`);
  if (calc.varAn !== null && calc.varAn < -3) alertes.push(`Masse en baisse de ${pct(calc.varAn)} sur un an a periode comparable.`);
  if (calc.poidsAbsences > 3) alertes.push(`Les absences pesent ${calc.poidsAbsences.toFixed(1)} % du brut sur le mois.`);
  const nonRenseigne = data.contrats.find(c => c.type === 'Non renseigne')?.n || 0;
  const totalContrats = data.contrats.reduce((s, c) => s + c.n, 0);
  if (totalContrats && nonRenseigne / totalContrats > 0.5) alertes.push(`${nonRenseigne} salaries sur ${totalContrats} sans type de contrat renseigne : l'analyse CDI/CDD est impossible.`);

  return (
    <div className="ms-racine">
      <div className="ms-filtres">
        <span className="ms-filtres-titre">Perimetre</span>
        <button className={`ms-chip ${etabs.length === 0 ? 'active' : ''}`} onClick={() => setEtabs([])}>
          Tout le groupe
        </button>
        {parSociete.map(([societe, liste]) => (
          <span className="ms-groupe" key={societe}>
            <button className="ms-chip ms-chip-societe" onClick={() => selSociete(liste)}>{societe}</button>
            {liste.map(e => (
              <button key={e.etab} className={`ms-chip ${etabs.includes(e.etab) ? 'active' : ''}`}
                onClick={() => basculer(e.etab)} title={`${e.presents} personnes presentes`}>
                {e.etab}
              </button>
            ))}
          </span>
        ))}
        {chargement && <span className="ms-chargement">actualisation…</span>}
      </div>

      <div className="ms-periode">
        Situation au <strong>{MOIS_COURT[dernier.mois - 1]} {dernier.annee}</strong>
        {' · '}{data.mois.length} mois d'historique
        {etabs.length > 0 && ` · ${etabs.length} etablissement(s) selectionne(s)`}
      </div>

      <div className="ms-tuiles">
        <Tuile libelle="Masse salariale du mois" valeur={keur(dernier.masse)} variation={calc.varMasse}
          detail={`${keur(calc.masse12)} sur 12 mois`} />
        <Tuile libelle="Effectif paye" valeur={dernier.effectif} variation={calc.varEffectif}
          detail={`${calc.totalPresents} presents a ce jour`} />
        <Tuile libelle="Cout moyen par personne" valeur={eur(calc.coutMoyen)} variation={calc.varCoutMoyen} />
        <Tuile libelle="Taux de charges patronales" valeur={`${calc.tauxCharges.toFixed(1)} %`}
          detail="cotisations / brut" />
        <Tuile libelle="Anciennete mediane" valeur={`${calc.ancienneteAns.toFixed(1)} ans`}
          detail={`${Math.round(calc.partSenior)} % au-dela de 20 ans`} alerte={calc.partSenior >= 35} />
        <Tuile libelle="Poids des absences" valeur={`${calc.poidsAbsences.toFixed(1)} %`}
          detail="du brut du mois" alerte={calc.poidsAbsences > 3} />
      </div>

      {alertes.length > 0 && (
        <div className="ms-alertes">
          <div className="ms-alertes-titre">Points d'attention</div>
          <ul>{alertes.map((a, i) => <li key={i}>{a}</li>)}</ul>
        </div>
      )}

      <section className="ms-carte">
        <h3>Variation d'un mois sur l'autre</h3>
        <p className="ms-sous-titre">
          Ecart de masse par rapport au mois precedent · survolez pour savoir d'ou il vient
        </p>
        <Variations mois={data.mois} />
        <div className="ms-note">
          L'ecart se decompose en <b>effet effectif</b> (entrees et sorties, au cout moyen du mois
          precedent) et <b>effet cout unitaire</b> (ce que coute chaque personne). Les deux
          s'additionnent exactement a l'ecart total.
        </div>
      </section>

      <div className="ms-duo">
        <section className="ms-carte">
          <h3>Masse salariale mensuelle</h3>
          <p className="ms-sous-titre">Cout total employeur, cotisations comprises</p>
          <div className="ms-legende">
            <span><i className="ms-puce" style={{ background: 'var(--serie-1)' }} />Masse totale</span>
            <span><i className="ms-puce" style={{ background: 'var(--serie-3)' }} />Hors primes annuelles et exceptionnel</span>
          </div>
          <Courbe
            points={data.mois.map(m => ({
              libelle: `${MOIS_COURT[m.mois - 1]} ${String(m.annee).slice(2)}`,
              masse: m.masse, recurrent: recurrent(m),
            }))}
            series={[
              { cle: 'masse', label: 'Masse totale', couleur: 'var(--serie-1)' },
              { cle: 'recurrent', label: 'Recurrent', couleur: 'var(--serie-3)', pointille: true },
            ]}
            format={keur} />
        </section>
        <section className="ms-carte">
          <h3>Effectif paye</h3>
          <p className="ms-sous-titre">Personnes ayant une ligne de paie sur le mois · echelle resserree</p>
          <Courbe
            points={data.mois.map(m => ({ libelle: `${MOIS_COURT[m.mois - 1]} ${String(m.annee).slice(2)}`, effectif: m.effectif }))}
            series={[{ cle: 'effectif', label: 'Effectif', couleur: 'var(--serie-3)' }]}
            format={(v) => Math.round(v)} baseZero={false} />
        </section>
      </div>

      <section className="ms-carte">
        <h3>Structure du cout</h3>
        <p className="ms-sous-titre">12 derniers mois · survolez une colonne pour le detail</p>
        <div className="ms-legende">
          {NATURES.map(n => (
            <span key={n.cle}><i className="ms-puce" style={{ background: n.couleur }} />{n.label}</span>
          ))}
        </div>
        <BarresEmpilees mois={douzeMois} />
        <div className="ms-note">
          Les absences ({keur(dernier.absences)} sur le mois) sont retranchees du brut et n'apparaissent pas dans les barres.
          Les <b>primes annuelles</b> sont isolees : c'est la que tombe le 13e mois, qui double la masse de decembre.
        </div>
      </section>

      <section className="ms-carte">
        <h3>Comparatif par service et par concession</h3>
        <p className="ms-sous-titre">
          Cout total cumule sur la periode, tous salaries confondus · postes regroupes par activite
        </p>
        <div className="ms-periode-choix">
          <span>Periode</span>
          <select value={periode.debut || moisDispo[0]?.cle || ''}
            onChange={e => setPeriode(p => ({ debut: e.target.value, fin: p.fin || moisDispo[moisDispo.length - 1]?.cle }))}>
            {moisDispo.map(m => <option key={m.cle} value={m.cle}>{m.label}</option>)}
          </select>
          <span>a</span>
          <select value={periode.fin || moisDispo[moisDispo.length - 1]?.cle || ''}
            onChange={e => setPeriode(p => ({ debut: p.debut || moisDispo[0]?.cle, fin: e.target.value }))}>
            {moisDispo.map(m => <option key={m.cle} value={m.cle}>{m.label}</option>)}
          </select>
          <button className="ms-periode-raccourci" onClick={() => setPeriode({ debut: '', fin: '' })}>
            12 derniers mois
          </button>
          {moisDispo.length > 0 && (
            <button className="ms-periode-raccourci"
              onClick={() => setPeriode({ debut: moisDispo[0].cle, fin: moisDispo[moisDispo.length - 1].cle })}>
              Tout l'historique
            </button>
          )}
        </div>
        <Comparatif entites={data.comparatif?.entites || []} groupes={data.comparatif?.groupes || []} />
      </section>

      <div className="ms-duo">
        <section className="ms-carte">
          <h3>Repartition par activite</h3>
          <p className="ms-sous-titre">{MOIS_COURT[dernier.mois - 1]} {dernier.annee}</p>
          <BarresHorizontales couleur="var(--serie-1)" format={keur}
            lignes={data.activites.map(a => ({
              libelle: a.libelle, valeur: a.masse,
              detail: `${a.effectif} pers. · ${eur(a.effectif ? a.masse / a.effectif : 0)} / pers.`,
            }))} />
        </section>
        <section className="ms-carte">
          <h3>Pyramide d'anciennete</h3>
          <p className="ms-sous-titre">Salaries presents a ce jour</p>
          <BarresHorizontales couleur="var(--serie-4)" format={(v) => `${v}`}
            lignes={['Moins d\'1 an', '1 a 3 ans', '3 a 10 ans', '10 a 20 ans', 'Plus de 20 ans', 'Inconnue']
              .map(t => ({ libelle: t, valeur: data.anciennete.find(a => a.tranche === t)?.n || 0 }))
              .filter(l => l.valeur > 0)} />
        </section>
      </div>

    </div>
  );
}
